import {
  createContext,
  MouseEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { MenuLayerLevel } from '../types';

type HoveredLayerLevel = MenuLayerLevel | null;

type LayerMouseEnterHandlerFactory = (
  level: MenuLayerLevel,
) => (event?: MouseEvent) => void;

export interface LeftMenuStateAPI {
  collapsed: boolean;
  expanded: boolean;
  toggleExpanded: () => void;
  resetExpanded: () => void;

  hoveredLayerLevel: HoveredLayerLevel;
  createLayerMouseEnterHandler: LayerMouseEnterHandlerFactory;
  resetHoveredLayerLevel: () => void;
}

const noopHandler = (): void => undefined;

export const useLeftMenuStateAPIFactory = (activeLevel: MenuLayerLevel): LeftMenuStateAPI => {
  const [
    hoveredLayerLevel,
    setHoveredLayerLevel,
  ] = useState<HoveredLayerLevel>(null);

  const expanded = (hoveredLayerLevel === '0');
  const rootOnly = (activeLevel === '0');
  const collapsed = !(rootOnly || expanded);

  const toggleExpanded = useCallback((): void => {
    !expanded
      ? setHoveredLayerLevel('0')
      : setHoveredLayerLevel(null);
  }, [expanded]);

  const resetExpanded = useCallback((): void => {
    setHoveredLayerLevel(null);
  }, []);

  // --

  const createLayerMouseEnterHandler = useCallback<LayerMouseEnterHandlerFactory>(
    (level: MenuLayerLevel): () => void => {
      return (
        !expanded
          ? (): void => setHoveredLayerLevel(level)
          : noopHandler
      );
    },
    [expanded],
  );

  const resetHoveredLayerLevel = useCallback((): void => {
    !expanded && setHoveredLayerLevel(null);
  }, [expanded]);

  return useMemo((): LeftMenuStateAPI => ({
    collapsed,
    expanded,
    toggleExpanded,
    resetExpanded,

    hoveredLayerLevel,
    createLayerMouseEnterHandler,
    resetHoveredLayerLevel,
  }), [hoveredLayerLevel, collapsed]);
};

export const LeftMenuStateAPIContext = createContext<LeftMenuStateAPI>({
  collapsed: false,
  expanded: false,
  toggleExpanded: () => undefined,
  resetExpanded: () => undefined,

  hoveredLayerLevel: null,
  createLayerMouseEnterHandler: (): () => void => noopHandler,
  resetHoveredLayerLevel: () => undefined,
});

export const useLeftMenuStateAPI = (): LeftMenuStateAPI => {
  return useContext(LeftMenuStateAPIContext);
};
